import {
  IPracticeMigration,
  ISourceLinkOption,
  type ICustomMappingSourceOption,
} from '@principle-theorem/practice-migrations/interfaces';
import { WithRef } from '@principle-theorem/shared';
import { IBaseMigrationAppointmentBook } from '../../../interfaces';
import { AppointmentBookToPractitionerMappingHandler } from '../../../mappings/appointment-book-to-practitioner';
import {
  IOasisAppointmentBook,
  OasisAppointmentBook,
  PatientAppointmentBookSourceEntity,
} from '../../source/entities/appointment-book';
import { OasisStafferMappingHandler } from './staff';
import { ProviderSourceEntity } from '../../source/entities/providers';
import { sortBy } from 'lodash';

export class OasisAppointmentBookToPractitionerMappingHandler extends AppointmentBookToPractitionerMappingHandler<
  OasisAppointmentBook,
  PatientAppointmentBookSourceEntity
> {
  sourceEntity = new PatientAppointmentBookSourceEntity();
  providerEntity = new ProviderSourceEntity();

  translateFn = (
    record: IOasisAppointmentBook
  ): IBaseMigrationAppointmentBook => ({
    id: record.id.toString(),
    description: record.description,
  });

  override async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();
    const providerRecords = await this.providerEntity
      .getAllRecords$(migration)
      .toPromise();
    const options = records
      .map((record) => record.data)
      .map((record) => {
        const appointmentBook = this.translateFn(record);
        return {
          label: appointmentBook.description,
          value: this.sourceEntity.getSourceRecordId(record).toString(),
        };
      });

    const bookIds = options.map((option) => option.value);
    const providerOptions = providerRecords
      .map((record) => record.data)
      .filter((record) => !bookIds.includes(record.id.toString()))
      .map((record) => {
        return {
          label: record.name,
          value: this.providerEntity.getSourceRecordId(record).toString(),
        };
      });

    return sortBy([...options, ...providerOptions], 'label');
  }

  override async getSourceLinkOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ISourceLinkOption[]> {
    const handler = new OasisStafferMappingHandler();
    const options = await handler.getSourceOptions(migration);

    const omitOption = {
      value: '-1',
      label: 'Omit',
    };
    return [omitOption, ...options].map((option) => ({
      sourceIdentifier: option.value,
      sourceLabel: option.label,
      resourceType: handler.sourceEntity.sourceEntity.metadata.idPrefix,
    }));
  }
}
